"use client";

import { LogIn } from "lucide-react";
import Link from "next/link";
import { usePathname } from "next/navigation";

import { cn } from "@/lib/utils";

import { Button } from "@/components/ui/button";

export interface UnauthorizedStateProps {
  title?: string;
  description?: string;
  className?: string;
}

/**
 * The 401 state: the request was fine, the caller just isn't (or is no longer)
 * signed in. Distinct from `ErrorState` — "Something went wrong" is both wrong
 * and useless here, because retrying will never fix it. The only useful action
 * is to log in, so that's the button.
 *
 * Sends the user back where they were via `callbackUrl`, and routes admin paths
 * to the admin login (the two are separate credential providers).
 */
export function UnauthorizedState({
  title = "Please log in to view this page",
  description = "Your session has expired or you're not signed in. Log in to continue.",
  className,
}: UnauthorizedStateProps) {
  const pathname = usePathname();
  const isAdmin = pathname?.startsWith("/admin") ?? false;
  const loginPath = isAdmin ? "/admin/login" : "/login";
  const href = pathname
    ? `${loginPath}?callbackUrl=${encodeURIComponent(pathname)}`
    : loginPath;

  return (
    <div
      role="alert"
      className={cn(
        "flex flex-col items-center justify-center gap-3 px-6 py-12 text-center",
        className,
      )}
    >
      <div className="flex size-12 items-center justify-center rounded-full bg-muted text-muted-foreground">
        <LogIn className="size-6" aria-hidden="true" />
      </div>
      <div className="space-y-1">
        <h3 className="text-base font-semibold">{title}</h3>
        <p className="mx-auto max-w-sm text-sm text-muted-foreground">
          {description}
        </p>
      </div>
      <Button asChild size="sm" className="mt-2">
        <Link href={href}>Log in</Link>
      </Button>
    </div>
  );
}
